import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { AshokaChakra } from "@/components/AshokaChakra";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back, author");
      navigate("/admin");
    } catch (err) {
      setError(apiError(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center px-5 py-16" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3" data-testid="login-logo-link">
          <AshokaChakra className="h-10 w-10 text-[var(--chakra)]" />
          <span className="font-devanagari text-3xl text-[var(--chakra-deep)]">भारत गाथा</span>
        </Link>
        <div className="mt-8 overflow-hidden rounded-3xl border border-[var(--line)] bg-white shadow-xl shadow-orange-100/60">
          <div className="tricolor-bar h-1.5" />
          <form onSubmit={submit} className="p-8">
            <p className="eyebrow text-[var(--saffron-deep)]">Author access</p>
            <h1 className="mt-2 font-serif text-3xl font-bold text-[var(--chakra-deep)]">Sign in to write</h1>
            <label className="mt-8 block text-sm font-medium text-stone-700">
              Email
              <input
                data-testid="login-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-[var(--line)] bg-[var(--paper)] px-4 py-3 text-base outline-none transition-colors focus:border-[var(--chakra)] focus:ring-2 focus:ring-[var(--chakra-tint)]"
                placeholder="you@example.com"
              />
            </label>
            <label className="mt-5 block text-sm font-medium text-stone-700">
              Password
              <input
                data-testid="login-password-input"
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-[var(--line)] bg-[var(--paper)] px-4 py-3 text-base outline-none transition-colors focus:border-[var(--chakra)] focus:ring-2 focus:ring-[var(--chakra-tint)]"
                placeholder="••••••••"
              />
            </label>
            {error && <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{error}</p>}
            <button
              type="submit" disabled={busy} data-testid="login-submit-btn"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--chakra)] py-3 text-sm font-semibold text-white transition-all hover:bg-[var(--chakra-deep)] disabled:opacity-60"
            >
              <LogIn className="h-4 w-4" /> {busy ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
        <p className="mt-6 text-center text-sm text-stone-500">
          Just here to read? <Link to="/" className="font-medium text-[var(--saffron-deep)] underline">Go to the blog</Link>
        </p>
      </div>
    </div>
  );
}
